import type React from "react";
import type { ThemePreference } from "../hooks/useThemePreference";

interface AppearanceSelectorProps {
  value: ThemePreference;
  onChange: (preference: ThemePreference) => void;
}

const options = [
  { value: "light", label: "Light", icon: <SunIcon /> },
  { value: "dark", label: "Dark", icon: <MoonIcon /> },
  { value: "system", label: "System", icon: <SystemIcon /> },
] as const;

export default function AppearanceSelector({ value, onChange }: AppearanceSelectorProps) {
  return (
    <div className="dropdown-menu-item-base dropdown-menu-item-bare" style={containerStyle}>
      <span style={labelStyle}>Theme</span>
      <div style={optionsStyle} aria-label="Theme">
        {options.map(({ value: preference, label, icon }) => (
          <button
            key={preference}
            type="button"
            aria-label={label}
            title={label}
            aria-pressed={value === preference}
            onClick={() => onChange(preference)}
            style={{
              ...optionStyle,
              ...(value === preference ? selectedOptionStyle : {}),
            }}
          >
            {icon}
          </button>
        ))}
      </div>
    </div>
  );
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 2v2.2M12 19.8V22M4.93 4.93l1.56 1.56M17.51 17.51l1.56 1.56M2 12h2.2M19.8 12H22M4.93 19.07l1.56-1.56M17.51 6.49l1.56-1.56"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20.4 15.3A8.5 8.5 0 0 1 8.7 3.6 8.5 8.5 0 1 0 20.4 15.3Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SystemIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

const containerStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  minHeight: "2rem",
};

const labelStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 400,
};

const optionsStyle: React.CSSProperties = {
  display: "inline-flex",
  flexShrink: 0,
  gap: 2,
  padding: 2,
  border: "1px solid var(--dialog-border-color)",
  borderRadius: 8,
  background: "transparent",
};

const optionStyle: React.CSSProperties = {
  width: 32,
  height: 24,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: "none",
  borderRadius: 6,
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
};

const selectedOptionStyle: React.CSSProperties = {
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
};
